import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Stars, ArrowLeft } from 'lucide-react';
import UserRegistrationForm from '../components/UserRegistrationForm';
import { authService, userService } from '../services/api';
import { trackRegistration } from '../utils/analytics';

interface UserRegistrationData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  birthDate: string;
  gender: string;
  birthCity: string;
  birthState?: string;
  birthCountry: string;
  birthTime?: string;
  birthLatitude?: number;
  birthLongitude?: number;
  birthTimezone?: string;
  maritalStatus?: string;
  profession?: string;
  mainInterests: string[];
  currentGoals: string[];
}

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      localStorage.setItem('pending_referral', ref.trim().toUpperCase());
    }
  }, []);

  const handleRegistration = async (data: UserRegistrationData) => {
    setLoading(true);
    setError(null);

    try {
      // Checar disponibilidade do email antes de submeter
      const check = await authService.checkEmail(data.email);
      if (!check.success) {
        setError(check.message || 'Email já está em uso');
        setLoading(false);
        return;
      }

      // Preparar dados para o backend
      const registrationData = {
        // Dados básicos do usuário
        name: data.name,
        email: data.email,
        password: data.password,
        
        // Dados do perfil
        profile: {
          birth_date: data.birthDate,
          gender: data.gender,
          birth_city: data.birthCity,
          birth_state: data.birthState || null,
          birth_country: data.birthCountry,
          birth_time: (data.birthTime && /^\d{2}:\d{2}$/.test(data.birthTime)) ? data.birthTime : null,
          birth_latitude: data.birthLatitude || null,
          birth_longitude: data.birthLongitude || null,
          birth_timezone: data.birthTimezone || null,
          marital_status: data.maritalStatus || null,
          profession: data.profession || null,
          main_interests: data.mainInterests,
          current_goals: data.currentGoals
        }
      };

      // Chamar API de registro
      const response = await authService.registerWithProfile(registrationData);
      if (response.access_token) {
        trackRegistration();
        localStorage.setItem('token', response.access_token);
        if (response.user) {
          localStorage.setItem('user', JSON.stringify(response.user));
        }
        // Aplicar código de indicação se houver
        const pendingRef = localStorage.getItem('pending_referral');
        if (pendingRef) {
          try {
            await userService.applyReferralCode(pendingRef);
          } catch {}
          localStorage.removeItem('pending_referral');
        }
        navigate('/verify');
        return;
      }
    } catch (err: any) {
      console.error('Erro no registro:', err);
      setError(
        err.response?.data?.detail || 
        'Erro ao criar conta. Tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
            <Stars className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Conta Criada com Sucesso!</h2>
            <p className="text-purple-200 mb-4">
              Bem-vindo ao mundo da astrologia e autoconhecimento.
            </p>
            <p className="text-sm text-purple-300">
              Redirecionando para seu painel...
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <Link
            to="/"
            className="inline-flex items-center text-purple-200 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao início
          </Link>
          
          <h1 className="text-4xl font-bold text-white mb-4">
            Crie Sua Conta
          </h1>
          <p className="text-xl text-purple-200 max-w-2xl mx-auto">
            Junte-se à nossa comunidade e descubra os segredos do universo através da astrologia personalizada
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Formulário */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            {error && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
                <p className="text-red-200 text-sm">{error}</p>
              </div>
            )}
            
            <UserRegistrationForm 
              onSubmit={handleRegistration}
              loading={loading}
              onAnyChange={() => setError(null)}
            />
            
            <div className="text-center mt-6">
              <p className="text-purple-200">
                Já tem uma conta?{' '}
                <Link 
                  to="/login" 
                  className="text-purple-300 hover:text-white transition-colors font-medium"
                >
                  Faça login
                </Link>
              </p>
            </div>
          </motion.div>

          {/* Informações sobre os benefícios */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-6"
          >
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-4">
                O que você ganha ao se cadastrar:
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="font-semibold text-white">Mapa Astral Personalizado</h4>
                    <p className="text-purple-200 text-sm">
                      Descubra sua personalidade através dos astros com base em sua data, horário e local de nascimento
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="font-semibold text-white">Leituras de Tarot Ilimitadas</h4>
                    <p className="text-purple-200 text-sm">
                      Acesso completo a diferentes tipos de tiragens e interpretações personalizadas
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="font-semibold text-white">Compatibilidade Astrológica</h4>
                    <p className="text-purple-200 text-sm">
                      Descubra sua compatibilidade com outras pessoas baseada nos signos
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="font-semibold text-white">Histórico de Consultas</h4>
                    <p className="text-purple-200 text-sm">
                      Mantenha um registro de todas suas leituras e acompanhe sua jornada
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-md rounded-2xl p-6 border border-white/20">
              <h3 className="text-lg font-bold text-white mb-2">
                🔮 Dica Especial
              </h3>
              <p className="text-purple-200 text-sm">
                Para um mapa astral mais preciso, tente descobrir o horário exato do seu nascimento. 
                Consulte sua certidão de nascimento ou pergunte aos seus pais!
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;